import { Case, CaseRequest, CaseStatus, CasePreservationist } from '../types/Case';
import type { CreateCaseRequestPayload, UpdateCaseRequestPayload } from '../types/Case';
import type { ServiceSummary } from '../types/Case';
import type { CaseRequestChangeLogEntry, CaseChangeLogEntry } from '../types/Case';
import type {
  HeartService,
  LungService,
  LiverService,
  LiverPumpingService,
  KidneyService,
  KidneyPancreasService,
  LivingDonorService,
  LogisticsOnlyService,
  DiscardService,
  ServiceChangeLogEntry
} from '../types/ServiceTypes';
import { logAPI } from '../utils/logger';
import { API_BASE_URL } from '../config/apiConfig';
import { getStorageItem, STORAGE_KEYS } from '../utils/storage';
import { withRetry } from '../utils/apiRetry';
import { showWarningNotification } from '../constants/uiConstants';
import { getAccessToken } from '../utils/authHelper';

// API error response types
interface ErrorResponse {
  message?: string;
  errors?: string[];
  // ASP.NET Core ProblemDetails fields (application/problem+json)
  title?: string;
  detail?: string;
  [key: string]: unknown;
}

// Generic API error handler
class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Merge conflict error with additional data
interface MergeConflictError extends ApiError {
  requiresConfirmation: boolean;
  existingCases: Case[];
  message: string;
}

// Generic fetch wrapper with error handling
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const start = Date.now();

  const userHeader = getStorageItem(STORAGE_KEYS.USER); // sessionStorage for security

  // Get auth token for API requests
  const token = await getAccessToken();

  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      // Attach optional user header for backend attribution if available
      ...(userHeader ? { 'X-User': userHeader } : {}),
      // Add auth token
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  };

  try {
    logAPI.debug('request', { url, method: config.method || 'GET' });
    const response = await fetch(url, config);

    if (!response.ok) {
      let serverMsg = `HTTP Error: ${response.status}`;
      try {
        const ct = response.headers.get('content-type') || '';
        // Match both 'application/json' and ASP.NET's 'application/problem+json' (ProblemDetails)
        if (ct.includes('json')) {
          const body = await response.json() as ErrorResponse;
          if (body && typeof body === 'object') {
            if (Array.isArray(body.errors)) {
              serverMsg = body.errors.join('; ');
            } else if (typeof body.message === 'string') {
              serverMsg = body.message;
              // Also include error details if available
              if (typeof body.error === 'string') {
                serverMsg += `: ${body.error}`;
              }
            } else if (typeof body.title === 'string') {
              // ProblemDetails responses use 'title' (and optional 'detail') rather than 'message'
              serverMsg = body.title;
              if (typeof body.detail === 'string') {
                serverMsg += `: ${body.detail}`;
              }
            }
            // If none of the known fields are present, keep the default
            // `HTTP Error: <status>` message — never surface the raw JSON body.
          }
        } else {
          const text = await response.text();
          if (text) serverMsg = text;
        }
      } catch (parseError) {
        // Intentionally ignore parse errors - we have a default error message
        logAPI.debug('Failed to parse error response body, using default message', parseError);
      }
      const err = new ApiError(response.status, serverMsg);
      logAPI.warn('response error', { url, status: response.status, message: serverMsg });
      throw err;
    }

    // Gracefully handle empty/204 responses
    const status = response.status;
    const contentLength = response.headers.get('content-length');
    const contentType = response.headers.get('content-type') || '';
    let data: T | string | undefined = undefined;

    const hasBody = status !== 204 && contentLength !== '0';
    if (hasBody) {
      if (contentType.includes('application/json')) {
        data = await response.json() as T;
      } else {
        // Fallback: consume text to avoid reader lock
        data = await response.text();
      }
    }

    logAPI.debug('response', { url, ms: Date.now() - start });
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      logAPI.error('api error', { url, message: error.message });
      throw error;
    }
    const msg = `Network Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
    logAPI.error('network error', { url, message: msg });
    throw new ApiError(0, msg);
  }
}

// API request with automatic retry for transient failures
async function apiRequestWithRetry<T>(
  endpoint: string,
  options: RequestInit = {},
  retryOptions?: { maxRetries?: number; showRetryNotification?: boolean }
): Promise<T> {
  const { maxRetries = 2, showRetryNotification = false } = retryOptions || {};

  return withRetry(
    () => apiRequest<T>(endpoint, options),
    {
      maxRetries,
      onRetry: (attempt) => {
        if (showRetryNotification) {
          showWarningNotification(`Connection issue. Retrying... (${attempt}/${maxRetries})`);
        }
        logAPI.info(`Retrying API request to ${endpoint}`, { attempt, maxRetries });
      },
    }
  );
}

// ============================================================================
// CASE API FUNCTIONS
// ============================================================================

export const caseApi = {
  // Get all cases with optional query parameters
  getAllCases: async (params?: {
    organ?: string;
    opo?: string;
    disposition?: string;
    search?: string;
    statusId?: number;
    unosId?: string;
    _sort?: string;
    _order?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
    dateRangeDays?: number;
  }): Promise<{ data: Case[]; pagination?: { currentPage: number; pageSize: number; totalCount: number; totalPages: number; hasNextPage: boolean; hasPreviousPage: boolean } }> => {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.append(key, value.toString());
        }
      });
    }

    const query = searchParams.toString();
    const endpoint = query ? `/cases?${query}` : '/cases';

    // Use retry for critical read operation
    const response = await apiRequestWithRetry<{ data: Case[]; pagination?: any }>(endpoint, {}, { maxRetries: 2 });

    // Return paginated response if available, otherwise wrap array in { data } for backward compatibility
    return response;
  },

  // Get a single case by ID
  getCaseById: async (id: string): Promise<Case> => {
    return apiRequestWithRetry<Case>(`/cases/${id}`, {}, { maxRetries: 2 });
  },
  // Get a single case by UNOS ID
  getCaseByUnos: async (unosId: string): Promise<Case> => {
    return apiRequestWithRetry<Case>(`/cases/by-unos/${encodeURIComponent(unosId)}`, {}, { maxRetries: 2 });
  },

  // Create a new case
  createCase: async (caseData: Omit<Case, 'id'>): Promise<Case> => {
    return apiRequest<Case>('/cases', {
      method: 'POST',
      body: JSON.stringify(caseData),
    });
  },

  // Update an existing case
  updateCase: async (id: number, caseData: Partial<Case>): Promise<Case> => {
    return apiRequest<Case>(`/cases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(caseData),
    });
  },

  // Partially update a case (PATCH)
  patchCase: async (id: number, updates: Partial<Case>): Promise<Case> => {
    return apiRequest<Case>(`/cases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  // Update case status
  updateCaseStatus: async (caseId: number, statusId: number): Promise<Case> => {
    return apiRequest<Case>(`/cases/${caseId}`, {
      method: 'PATCH',
      body: JSON.stringify({ statusId }),
    });
  },

  // Admin override case status with required reason
  adminOverrideStatus: async (caseId: number, statusId: number, reason: string): Promise<Case> => {
    return apiRequest<Case>(`/cases/${caseId}`, {
      method: 'PATCH',
      body: JSON.stringify({ statusId, statusChangeReason: reason }),
    });
  },

  // Get billing cases - optimized endpoint for billing screen
  // showOnlyBilled: false = Ready for Billing only, true = Billed only
  getBillingCases: async (showOnlyBilled: boolean = false): Promise<Array<{
    id: number;
    unosId: string;
    caseCompletionDate: string;
    location: string;
    organ: string | null;
    lateralityId: number | null;
    disposition: string;
    serviceType: string;
    organForService: string;
    transportDevice: string;
    statusId: number;
    serviceCount: number;
    totalAmountBilled: number | null;
  }>> => {
    const params = new URLSearchParams();
    if (showOnlyBilled) {
      params.append('showOnlyBilled', 'true');
    }
    const query = params.toString();
    const endpoint = query ? `/cases/billing?${query}` : '/cases/billing';
    return apiRequestWithRetry(endpoint, {}, { maxRetries: 2 });
  },

  // Full-text search across Cases, CaseRequests, and Services
  // Returns Case IDs that match the search term
  fullTextSearch: async (searchTerm: string): Promise<number[]> => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }
    const params = new URLSearchParams({ q: searchTerm.trim() });
    return apiRequest<number[]>(`/cases/search?${params.toString()}`);
  },

  // Delete a case
  deleteCase: async (id: number): Promise<void> => {
    await apiRequest<void>(`/cases/${id}`, {
      method: 'DELETE',
    });
  },

  // Get change log for a case
  getChangeLog: async (id: number): Promise<CaseChangeLogEntry[]> => {
    return apiRequest<CaseChangeLogEntry[]>(`/cases/${id}/change-log`);
  },

  // Update preservationists for a case (replaces all existing preservationists)
  // The order of the array determines priority - first item is primary
  updatePreservationists: async (caseId: number, preservationists: Array<{ opcsContactId: number }>): Promise<CasePreservationist[]> => {
    return apiRequest<CasePreservationist[]>(`/cases/${caseId}/preservationists`, {
      method: 'PUT',
      body: JSON.stringify(preservationists),
    });
  },

  // Search cases by multiple criteria
  searchCases: async (searchCriteria: {
    unosId?: string;
    organ?: string;
    opo?: string;
    disposition?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Case[]> => {
    const params = new URLSearchParams();
    
    Object.entries(searchCriteria).forEach(([key, value]) => {
      if (value) {
        if (key === 'dateFrom') {
          params.append('date_gte', value);
        } else if (key === 'dateTo') {
          params.append('date_lte', value);
        } else {
          params.append(`${key}_like`, value);
        }
      }
    });
    
    return apiRequest<Case[]>(`/cases?${params.toString()}`);
  },

  // Get cases by date range
  getCasesByDateRange: async (startDate: string, endDate: string): Promise<Case[]> => {
    return apiRequest<Case[]>(`/cases?date_gte=${startDate}&date_lte=${endDate}`);
  },

  // Get cases by organ type
  getCasesByOrgan: async (organ: string): Promise<Case[]> => {
    return apiRequest<Case[]>(`/cases?organ=${organ}`);
  },

  // Get cases by OPO
  getCasesByOpo: async (opo: string): Promise<Case[]> => {
    return apiRequest<Case[]>(`/cases?opo=${opo}`);
  },

  // Get cases by disposition
  getCasesByDisposition: async (disposition: string): Promise<Case[]> => {
    return apiRequest<Case[]>(`/cases?disposition=${disposition}`);
  },

  // Get case statistics
  getCaseStats: async (): Promise<{
    total: number;
    byOrgan: Record<string, number>;
    byOpo: Record<string, number>;
    byDisposition: Record<string, number>;
  }> => {
    const cases = await apiRequest<Case[]>('/cases');
    
    const stats = {
      total: cases.length,
      byOrgan: {} as Record<string, number>,
      byOpo: {} as Record<string, number>,
      byDisposition: {} as Record<string, number>,
    };
    
    cases.forEach(caseItem => {
      // Count by organ
      const organKey = caseItem.organ && caseItem.organ.trim().length > 0 ? caseItem.organ : 'Unknown';
      stats.byOrgan[organKey] = (stats.byOrgan[organKey] || 0) + 1;
      
      // Count by OPO
      const opoKey = caseItem.opo && caseItem.opo.trim().length > 0 ? caseItem.opo : 'Unknown';
      stats.byOpo[opoKey] = (stats.byOpo[opoKey] || 0) + 1;
      
      // Count by disposition
      const dispositionKey = caseItem.disposition && caseItem.disposition.trim().length > 0 ? caseItem.disposition : 'Unknown';
      stats.byDisposition[dispositionKey] = (stats.byDisposition[dispositionKey] || 0) + 1;
    });
    
    return stats;
  },
};

// ============================================================================
// CASE REQUEST API FUNCTIONS
// ============================================================================

export const caseRequestApi = {
  // Get all case requests
  getAllCaseRequests: async (params?: {
    status?: string;
    _sort?: string;
    _order?: 'asc' | 'desc';
    _page?: number;
    _limit?: number;
  }): Promise<CaseRequest[]> => {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    const query = searchParams.toString();
    const endpoint = query ? `/caseRequests?${query}` : '/caseRequests';

    // Use retry for critical read operation
    return apiRequestWithRetry<CaseRequest[]>(endpoint, {}, { maxRetries: 2 });
  },

  // Get a single case request by ID
  getCaseRequestById: async (id: string | number): Promise<CaseRequest> => {
    return apiRequestWithRetry<CaseRequest>(`/caseRequests/${id}`, {}, { maxRetries: 2 });
  },

  // Create a new case request
  createCaseRequest: async (requestData: CreateCaseRequestPayload): Promise<CaseRequest> => {
    return apiRequest<CaseRequest>('/caseRequests', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  },

  // Update an existing case request
  updateCaseRequest: async (id: string | number, requestData: UpdateCaseRequestPayload): Promise<CaseRequest> => {
    return apiRequest<CaseRequest>(`/caseRequests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(requestData),
    });
  },

  // Delete a case request
  deleteCaseRequest: async (id: string | number): Promise<void> => {
    await apiRequest<void>(`/caseRequests/${id}`, {
      method: 'DELETE',
    });
  },

  // Get pending case requests
  getPendingCaseRequests: async (): Promise<CaseRequest[]> => {
    return apiRequest<CaseRequest[]>('/caseRequests?status=pending');
  },

  // Get approved case requests
  getApprovedCaseRequests: async (): Promise<CaseRequest[]> => {
    return apiRequest<CaseRequest[]>('/caseRequests?status=approved');
  },

  // Get rejected case requests
  getRejectedCaseRequests: async (): Promise<CaseRequest[]> => {
    return apiRequest<CaseRequest[]>('/caseRequests?status=rejected');
  },

  // Convert case request to case
  convertRequestToCase: async (requestId: number, caseData: Omit<Case, 'id'>, forceCreate = false): Promise<{ case: Case; request: CaseRequest }> => {
    // Use the merge endpoint instead of general case creation
    const mergeResponse = await apiRequest<any>(`/caseRequests/${requestId}/merge`, {
      method: 'POST',
      body: JSON.stringify({ ...caseData, forceCreate }),
    });

    // If confirmation is required, throw a specific error with the data
    if (mergeResponse.requiresConfirmation) {
      const error = new Error('Confirmation required');
      const conflictError = error as MergeConflictError;
      conflictError.requiresConfirmation = true;
      conflictError.existingCases = mergeResponse.existingCases;
      conflictError.message = mergeResponse.message;
      throw error;
    }

    const newCase = mergeResponse;

    // First, fetch the complete case request data to preserve it
    const existingRequest = await caseRequestApi.getCaseRequestById(requestId);

    // Note: Service records are now created manually through service-specific forms
    // after the case is merged, not automatically during the merge process.

    // Update the request to mark it as merged while preserving all original data
    const updatedRequest = await caseRequestApi.updateCaseRequest(requestId, {
      ...existingRequest, // Preserve all original data
      status: 'merged',
      convertedToCaseId: newCase.id,
      reviewDateTime: new Date().toISOString(),
    });
    
    return { case: newCase, request: updatedRequest };
  },

  // Approve a case request
  approveCaseRequest: async (id: number, reviewedBy: string, notes?: string): Promise<CaseRequest> => {
    return apiRequest<CaseRequest>(`/caseRequests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'approved',
        reviewedBy,
        reviewDateTime: new Date().toISOString(),
        notes: notes || '',
      }),
    });
  },

  // Reject a case request
  rejectCaseRequest: async (id: number, reviewedBy: string, notes: string): Promise<CaseRequest> => {
    return apiRequest<CaseRequest>(`/caseRequests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'rejected',
        reviewedBy,
        reviewDateTime: new Date().toISOString(),
        notes,
      }),
    });
  },

  // Merge a case request into a case (server decides create/update)
  mergeCaseRequest: async (id: number): Promise<{ caseId: number }> => {
    return apiRequest<{ caseId: number }>(`/caseRequests/${id}/merge`, {
      method: 'POST',
    });
  },

  // Get change log for a specific case request
  getChangeLog: async (id: number): Promise<CaseRequestChangeLogEntry[]> => {
    try {
      return await apiRequest<CaseRequestChangeLogEntry[]>(`/caseRequests/${id}/change-log`);
    } catch (err) {
      // Fallback to alternate route ordering if server expects /caseRequests/change-log/{id}
      if (err instanceof ApiError && err.status === 404) {
        try {
          return await apiRequest<CaseRequestChangeLogEntry[]>(`/caseRequests/change-log/${id}`);
        } catch (err2) {
          if (err2 instanceof ApiError && err2.status === 404) {
            // Final fallback: use dedicated controller path
            return await apiRequest<CaseRequestChangeLogEntry[]>(`/change-log/case-requests/${id}`);
          }
          throw err2;
        }
      }
      throw err;
    }
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const apiUtils = {
  // Test API connection
  testConnection: async (): Promise<boolean> => {
    try {
      await apiRequest('/cases?_limit=1');
      return true;
    } catch (error) {
      // Intentionally return false on any error - this is a connection test
      logAPI.debug('API connection test failed', error);
      return false;
    }
  },

  // Get API health status
  getHealthStatus: async (): Promise<{
    status: 'healthy' | 'unhealthy';
    timestamp: string;
    casesCount?: number;
    requestsCount?: number;
  }> => {
    try {
      const [cases, requests] = await Promise.all([
        apiRequest<Case[]>('/cases'),
        apiRequest<CaseRequest[]>('/caseRequests'),
      ]);
      
      return {
        status: 'healthy',
        timestamp: new Date().toISOString(),
        casesCount: cases.length,
        requestsCount: requests.length,
      };
    } catch (error) {
      // Intentionally return unhealthy status object on any error
      logAPI.warn('API health check failed', error);
      return {
        status: 'unhealthy',
        timestamp: new Date().toISOString(),
      };
    }
  },

  // Bulk operations
  bulkCreateCases: async (cases: Omit<Case, 'id'>[]): Promise<Case[]> => {
    const results = await Promise.all(
      cases.map(caseData => caseApi.createCase(caseData))
    );
    return results;
  },

  bulkUpdateCases: async (updates: { id: number; data: Partial<Case> }[]): Promise<Case[]> => {
    const results = await Promise.all(
      updates.map(({ id, data }) => caseApi.updateCase(id, data))
    );
    return results;
  },

  bulkDeleteCases: async (ids: number[]): Promise<void> => {
    await Promise.all(
      ids.map(id => caseApi.deleteCase(id))
    );
  },
};

// Export everything
export { ApiError };

const apiService = {
  caseApi,
  caseRequestApi,
  apiUtils,
};

export default apiService;

// ============================================================================
// SERVICES API FUNCTIONS (separate export to avoid breaking default aggregate)
// ============================================================================

export const servicesApi = {
  // Fetch services for a given caseId with names
  getByCase: async (caseId: number): Promise<ServiceSummary[]> => {
    return apiRequest<ServiceSummary[]>(`/services/by-case/${caseId}`);
  },
  // Fetch services for multiple cases at once (performance optimization)
  getBatchByCases: async (caseIds: number[]): Promise<Record<number, ServiceSummary[]>> => {
    const result = await apiRequest<Record<number, ServiceSummary[]>>(`/services/by-cases`, {
      method: 'POST',
      body: JSON.stringify(caseIds),
    });
    return result;
  },
  // Basic CRUD passthroughs if needed later
  // Note: This generic create method should not be used - use service-specific endpoints instead
  create: async (payload: Partial<ServiceSummary> & { caseId: number }): Promise<any> => {
    return apiRequest<any>(`/services`, { method: 'POST', body: JSON.stringify(payload) });
  },
  patch: async (
    id: number,
    payload: Partial<{
      description: string;
      occurredAt: string | null;
      organId: number | 0;
      dispositionId: number | 0;
      deviceUsed: boolean | null;
      deviceType: string | null;
      deviceTypeId: number | null;
      deviceSubTypeId: number | null;
    }>
  ): Promise<void> => {
    await apiRequest<void>(`/services/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/services/${id}`, { method: 'DELETE' });
  },
  // Update billing info for a service (AmountBilled and InvoiceNumber)
  updateBilling: async (serviceId: number, serviceType: string, amountBilled: number, invoiceNumber?: string): Promise<void> => {
    const payload: { serviceType: string; amountBilled: number; invoiceNumber?: string } = { serviceType, amountBilled };
    if (invoiceNumber) {
      payload.invoiceNumber = invoiceNumber;
    }
    await apiRequest<void>(`/services/${serviceId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
};

// ============================================================================
// SERVICE TYPE-SPECIFIC API FUNCTIONS
// ============================================================================

export const heartServicesApi = {
  getAll: async (caseId?: number): Promise<HeartService[]> => {
    const endpoint = caseId ? `/heartservices?caseId=${caseId}` : '/heartservices';
    return apiRequest<HeartService[]>(endpoint);
  },
  getById: async (id: number): Promise<HeartService> => {
    return apiRequest<HeartService>(`/heartservices/${id}`);
  },
  create: async (service: Omit<HeartService, 'id'>): Promise<HeartService> => {
    return apiRequest<HeartService>('/heartservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: HeartService): Promise<void> => {
    await apiRequest<void>(`/heartservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/heartservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/heartservices/${id}/change-log`);
  },
};

export const lungServicesApi = {
  getAll: async (caseId?: number): Promise<LungService[]> => {
    const endpoint = caseId ? `/lungservices?caseId=${caseId}` : '/lungservices';
    return apiRequest<LungService[]>(endpoint);
  },
  getById: async (id: number): Promise<LungService> => {
    return apiRequest<LungService>(`/lungservices/${id}`);
  },
  create: async (service: Omit<LungService, 'id'>): Promise<LungService> => {
    return apiRequest<LungService>('/lungservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: LungService): Promise<void> => {
    await apiRequest<void>(`/lungservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/lungservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/lungservices/${id}/change-log`);
  },
};

export const liverServicesApi = {
  getAll: async (caseId?: number): Promise<LiverService[]> => {
    const endpoint = caseId ? `/liverservices?caseId=${caseId}` : '/liverservices';
    return apiRequest<LiverService[]>(endpoint);
  },
  getById: async (id: number): Promise<LiverService> => {
    return apiRequest<LiverService>(`/liverservices/${id}`);
  },
  create: async (service: Omit<LiverService, 'id'>): Promise<LiverService> => {
    return apiRequest<LiverService>('/liverservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: LiverService): Promise<void> => {
    await apiRequest<void>(`/liverservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/liverservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/liverservices/${id}/change-log`);
  },
};

export const liverPumpingServicesApi = {
  getAll: async (caseId?: number): Promise<LiverPumpingService[]> => {
    const endpoint = caseId ? `/liverpumpingservices?caseId=${caseId}` : '/liverpumpingservices';
    return apiRequest<LiverPumpingService[]>(endpoint);
  },
  getById: async (id: number): Promise<LiverPumpingService> => {
    return apiRequest<LiverPumpingService>(`/liverpumpingservices/${id}`);
  },
  create: async (service: Omit<LiverPumpingService, 'id'>): Promise<LiverPumpingService> => {
    return apiRequest<LiverPumpingService>('/liverpumpingservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: LiverPumpingService): Promise<void> => {
    await apiRequest<void>(`/liverpumpingservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/liverpumpingservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/liverpumpingservices/${id}/change-log`);
  },
};

export const kidneyServicesApi = {
  getAll: async (caseId?: number): Promise<KidneyService[]> => {
    const endpoint = caseId ? `/kidneyservices?caseId=${caseId}` : '/kidneyservices';
    return apiRequest<KidneyService[]>(endpoint);
  },
  getById: async (id: number): Promise<KidneyService> => {
    return apiRequest<KidneyService>(`/kidneyservices/${id}`);
  },
  create: async (service: Omit<KidneyService, 'id'>): Promise<KidneyService> => {
    return apiRequest<KidneyService>('/kidneyservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: KidneyService): Promise<void> => {
    await apiRequest<void>(`/kidneyservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/kidneyservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/kidneyservices/${id}/change-log`);
  },
};

export const kidneyPancreasServicesApi = {
  getAll: async (caseId?: number): Promise<KidneyPancreasService[]> => {
    const endpoint = caseId ? `/kidneypancreasservices?caseId=${caseId}` : '/kidneypancreasservices';
    return apiRequest<KidneyPancreasService[]>(endpoint);
  },
  getById: async (id: number): Promise<KidneyPancreasService> => {
    return apiRequest<KidneyPancreasService>(`/kidneypancreasservices/${id}`);
  },
  create: async (service: Omit<KidneyPancreasService, 'id'>): Promise<KidneyPancreasService> => {
    return apiRequest<KidneyPancreasService>('/kidneypancreasservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: KidneyPancreasService): Promise<void> => {
    await apiRequest<void>(`/kidneypancreasservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/kidneypancreasservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/kidneypancreasservices/${id}/change-log`);
  },
};

export const livingDonorServicesApi = {
  getAll: async (caseId?: number): Promise<LivingDonorService[]> => {
    const endpoint = caseId ? `/livingdonorservices?caseId=${caseId}` : '/livingdonorservices';
    return apiRequest<LivingDonorService[]>(endpoint);
  },
  getById: async (id: number): Promise<LivingDonorService> => {
    return apiRequest<LivingDonorService>(`/livingdonorservices/${id}`);
  },
  create: async (service: Omit<LivingDonorService, 'id'>): Promise<LivingDonorService> => {
    return apiRequest<LivingDonorService>('/livingdonorservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: LivingDonorService): Promise<void> => {
    await apiRequest<void>(`/livingdonorservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/livingdonorservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/livingdonorservices/${id}/change-log`);
  },
};

export const logisticsOnlyServicesApi = {
  getAll: async (caseId?: number): Promise<LogisticsOnlyService[]> => {
    const endpoint = caseId ? `/logisticsonlyservices?caseId=${caseId}` : '/logisticsonlyservices';
    return apiRequest<LogisticsOnlyService[]>(endpoint);
  },
  getById: async (id: number): Promise<LogisticsOnlyService> => {
    return apiRequest<LogisticsOnlyService>(`/logisticsonlyservices/${id}`);
  },
  create: async (service: Omit<LogisticsOnlyService, 'id'>): Promise<LogisticsOnlyService> => {
    return apiRequest<LogisticsOnlyService>('/logisticsonlyservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: LogisticsOnlyService): Promise<void> => {
    await apiRequest<void>(`/logisticsonlyservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/logisticsonlyservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/logisticsonlyservices/${id}/change-log`);
  },
};

export const discardServicesApi = {
  getAll: async (caseId?: number): Promise<DiscardService[]> => {
    const endpoint = caseId ? `/discardservices?caseId=${caseId}` : '/discardservices';
    return apiRequest<DiscardService[]>(endpoint);
  },
  getById: async (id: number): Promise<DiscardService> => {
    return apiRequest<DiscardService>(`/discardservices/${id}`);
  },
  create: async (service: Omit<DiscardService, 'id'>): Promise<DiscardService> => {
    return apiRequest<DiscardService>('/discardservices', {
      method: 'POST',
      body: JSON.stringify(service),
    });
  },
  update: async (id: number, service: DiscardService): Promise<void> => {
    await apiRequest<void>(`/discardservices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(service),
    });
  },
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`/discardservices/${id}`, {
      method: 'DELETE',
    });
  },
  getChangeLog: async (id: number): Promise<ServiceChangeLogEntry[]> => {
    return apiRequest<ServiceChangeLogEntry[]>(`/discardservices/${id}/change-log`);
  },
};

// ============================================================================
// LOOKUPS API FUNCTIONS
// ============================================================================

export const lookupsApi = {
  getLocations: async (includeInactive = false): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest(`/lookups/locations?includeInactive=${includeInactive}`);
  },
  getOpos: async (includeInactive = false): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest(`/lookups/opos?includeInactive=${includeInactive}`);
  },
  getServiceTypes: async (): Promise<Array<{ id: number; name: string }>> => {
    return apiRequest('/lookups/service-types');
  },
  getCaseStatuses: async (includeInactive = false): Promise<CaseStatus[]> => {
    return apiRequest(`/lookups/case-statuses?includeInactive=${includeInactive}`);
  },
  getOrgans: async (): Promise<Array<{ id: number; name: string }>> => {
    return apiRequest('/lookups/organs');
  },
  getCaseRequestOrgans: async (includeInactive = false): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest(`/lookups/case-request-organs?includeInactive=${includeInactive}`);
  },
  getDiscardedOrgans: async (includeInactive = false): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest(`/lookups/discarded-organs?includeInactive=${includeInactive}`);
  },
  getDispositions: async (includeInactive = false): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest(`/lookups/dispositions?includeInactive=${includeInactive}`);
  },
  // Device types removed - return empty arrays to avoid 404 errors
  getDeviceTypes: async (): Promise<Array<{ id: number; name: string }>> => {
    return Promise.resolve([]);
  },
  getDeviceSubTypes: async (deviceTypeId?: number): Promise<Array<{ id: number; name: string; deviceTypeId: number }>> => {
    return Promise.resolve([]);
  },
  getOpcsContacts: async (includeInactive = false): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest(`/lookups/opcs-contacts?includeInactive=${includeInactive}`);
  },
  getDonorTypes: async (includeInactive = false): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest(`/lookups/donor-types?includeInactive=${includeInactive}`);
  },
  getLateralities: async (includeInactive = false): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest(`/lookups/lateralities?includeInactive=${includeInactive}`);
  },
  getArrivalMethods: async (): Promise<Array<{ id: number; name: string }>> => {
    return apiRequest('/lookups/arrival-methods');
  },
  getLiverPumpingDevices: async (): Promise<Array<{ id: number; name: string; isActive: boolean }>> => {
    return apiRequest('/lookups/liver-pumping-devices');
  },
};

// OPO Admin API
export interface OpoData {
  id: number;
  name: string;
  isDeprecated: boolean;
}

export const opoApi = {
  getAll: async (includeDeprecated = false): Promise<OpoData[]> => {
    return apiRequest(`/opos?includeDeprecated=${includeDeprecated}`);
  },
  getById: async (id: number): Promise<OpoData> => {
    return apiRequest(`/opos/${id}`);
  },
  create: async (name: string): Promise<OpoData> => {
    return apiRequest('/opos', {
      method: 'POST',
      body: JSON.stringify({ name, isDeprecated: false }),
    });
  },
  update: async (id: number, name: string): Promise<OpoData> => {
    return apiRequest(`/opos/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  },
  deprecate: async (id: number): Promise<void> => {
    return apiRequest(`/opos/${id}/deprecate`, {
      method: 'PUT',
    });
  },
  delete: async (id: number): Promise<void> => {
    return apiRequest(`/opos/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================================================
// SERVICE ATTACHMENTS API FUNCTIONS
// ============================================================================

export interface ServiceAttachment {
  id: number;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  uploadedAtUtc: string;
  uploadedBy: string | null;
  isImage: boolean;
  downloadUrl: string;
}

export const attachmentsApi = {
  /**
   * Get all attachments for a service
   */
  getAttachments: async (serviceType: string, serviceId: number): Promise<ServiceAttachment[]> => {
    return apiRequestWithRetry<ServiceAttachment[]>(
      `/services/${serviceType}/${serviceId}/attachments`,
      {},
      { maxRetries: 2 }
    );
  },

  /**
   * Upload a file attachment to a service.
   * Handles multipart/form-data upload.
   */
  uploadAttachment: async (
    serviceType: string,
    serviceId: number,
    file: File
  ): Promise<ServiceAttachment> => {
    const url = `${API_BASE_URL}/services/${serviceType}/${serviceId}/attachments`;
    const token = await getAccessToken();
    const userHeader = getStorageItem(STORAGE_KEYS.USER);

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(userHeader ? { 'X-User': userHeader } : {}),
        // Note: Don't set Content-Type for FormData - browser sets it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      let errorMessage = `Upload failed: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch {
        // Ignore parse errors
      }
      throw new ApiError(response.status, errorMessage);
    }

    return response.json();
  },

  /**
   * Get the download URL for an attachment (includes auth token)
   */
  getDownloadUrl: (serviceType: string, serviceId: number, attachmentId: number): string => {
    return `${API_BASE_URL}/services/${serviceType}/${serviceId}/attachments/${attachmentId}/download`;
  },

  /**
   * Download an attachment as a blob (useful for in-app viewing)
   */
  downloadAttachment: async (
    serviceType: string,
    serviceId: number,
    attachmentId: number
  ): Promise<Blob> => {
    const url = `${API_BASE_URL}/services/${serviceType}/${serviceId}/attachments/${attachmentId}/download`;
    const token = await getAccessToken();

    const response = await fetch(url, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new ApiError(response.status, `Download failed: ${response.status}`);
    }

    return response.blob();
  },

  /**
   * Delete an attachment (soft delete)
   */
  deleteAttachment: async (
    serviceType: string,
    serviceId: number,
    attachmentId: number
  ): Promise<void> => {
    await apiRequest<void>(
      `/services/${serviceType}/${serviceId}/attachments/${attachmentId}`,
      { method: 'DELETE' }
    );
  },
};

// --- Reports API ---

export interface ConsumableLinkageReportRow {
  caseId: number;
  unosId: string | null;
  caseDate: string | null;
  serviceType: string;
  consumableType: string;
  subType: string | null;
  lotNumber: string | null;
  serialNumber: string | null;
  expirationDate: string | null;
  isLinked: boolean;
  inventoryClaimId: string | null;
}

export const reportsApi = {
  getConsumableLinkage: async (params: {
    startDate?: string;
    endDate?: string;
    serviceType?: string;
    linkedStatus?: string;
    cutoffDate?: string;
  }): Promise<ConsumableLinkageReportRow[]> => {
    const searchParams = new URLSearchParams();
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.serviceType) searchParams.append('serviceType', params.serviceType);
    if (params.linkedStatus) searchParams.append('linkedStatus', params.linkedStatus);
    if (params.cutoffDate) searchParams.append('cutoffDate', params.cutoffDate);

    const query = searchParams.toString();
    return apiRequestWithRetry<ConsumableLinkageReportRow[]>(
      `/reports/consumable-linkage${query ? `?${query}` : ''}`,
      {},
      { maxRetries: 2 }
    );
  },
};
